import React, { memo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Collapse,
  Stack,
  IconButton,
  Tooltip,
  TextField,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  mdiChevronDown,
  mdiChevronUp,
  mdiWrenchOutline,
  mdiPencilOutline,
  mdiRefresh,
  mdiAlertCircleOutline,
} from '@mdi/js';
import MdiIcon from '../mdi/MdiIcon';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ChatSpinner } from './ChatSpinner';
import { ChatFileList } from './ChatFileList';
import { usePrefersReducedMotion } from '../../../hooks/usePrefersReducedMotion';
import type { ChatFile, ChatToolCall } from './types';

// The markdown renderer and its plugins are a sizeable dependency that most
// users never open the chat for, so it is loaded on demand.
const Markdown = React.lazy(() =>
  import('./Markdown').then((module) => ({ default: module.Markdown })),
);

// The table and the chart are a sizeable dependency too, and most replies
// have neither.
const ChatToolView = React.lazy(() =>
  import('./ChatToolView').then((module) => ({ default: module.ChatToolView })),
);

interface ChatMessageProps {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: ChatToolCall[];
  createdAt?: string;
  /**
   * False when the reply was cut short by Stop or by the provider.
   *
   * Undefined means complete, which is what every message sent before this
   * existed will look like.
   */
  complete?: boolean;
  /** Hidden while a stream is running so actions cannot race the response. */
  busy?: boolean;
  onEdit?: (messageId: string, content: string) => void;
  onRegenerate?: () => void;
  /** Called when the user asks for one of the reply's files. */
  onDownloadFile?: (file: ChatFile) => void;
}

const MessageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isUser',
})<{ isUser: boolean }>(({ isUser }) => ({
  display: 'flex',
  justifyContent: isUser ? 'flex-end' : 'flex-start',
  marginBottom: '12px',
}));

/**
 * The message bubble.
 *
 * Every value here comes from the theme: spacing and radii from the theme's own
 * scales, colours from `palette.chat`. This used to be a styled component with
 * hand-picked pixel values and a `palette.mode === 'dark'` branch picking grey
 * steps, which is what let the bubble go light inside a dark panel.
 *
 * It stays a scoped styled component rather than a `MuiPaper` styleOverride:
 * an override on `root` would apply to every `Paper` in the application, and
 * the chat is the only thing that wants this shape.
 */
const MessagePaper = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isUser',
})<{ isUser: boolean }>(({ isUser, theme }) => ({
  padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
  maxWidth: '100%',
  backgroundColor: isUser
    ? theme.palette.primary.main
    : theme.palette.chat?.bubble ?? theme.palette.grey[100],
  color: isUser
    ? theme.palette.primary.contrastText
    : theme.palette.text.primary,
  // The tail corner is squared off so the bubble points at its author.
  borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
  // `wordBreak: 'break-word'` is a legacy alias; `overflowWrap: 'anywhere'` is
  // the supported value and also breaks unbroken IDs/URLs that overflow bubbles.
  overflowWrap: 'anywhere',
  whiteSpace: 'pre-wrap',
}));

/** Collapsible disclosure for one tool call. */
function ToolCallPanel({ call }: { call: ChatToolCall }) {
  const [open, setOpen] = React.useState(false);
  const reducedMotion = usePrefersReducedMotion();

  const statusColor = call.running
    ? 'text.secondary'
    : call.ok === false
      ? 'error.main'
      : 'success.main';

  // The tool name is a machine identifier and stays as it is; the wording
  // around it is the reader's.
  const label = call.running
    ? t`${call.name} running…`
    : call.summary
      ? t`${call.name} — ${call.summary}`
      : call.name;

  return (
    <Box sx={{ mt: 1, mb: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          width: '100%',
          px: 1,
          // 4px of padding around a 14px icon put this at roughly 22px tall,
          // under the 24px WCAG 2.5.8 floor even before touch was considered.
          // The disclosure is a primary way of checking what the assistant did,
          // so it is sized to be comfortably hittable rather than compact.
          minHeight: 44,
          border: 0,
          background: 'transparent',
          cursor: 'pointer',
          color: 'text.secondary',
          font: 'inherit',
          textAlign: 'left',
        }}
      >
        <MdiIcon d={mdiWrenchOutline} size={14} />
        <Typography variant="caption" sx={{ flex: 1, color: statusColor }}>
          {label}
        </Typography>
        {call.running && <ChatSpinner size={12} />}
        <MdiIcon d={open ? mdiChevronUp : mdiChevronDown} size={16} />
      </Box>

      <Collapse in={open} timeout={reducedMotion ? 0 : undefined}>
        <Box sx={{ px: 1, pb: 1 }}>
          <Typography
            component="pre"
            variant="caption"
            sx={{ m: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.7rem' }}
          >
            {JSON.stringify(call.arguments ?? {}, null, 2)}
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
}

/** Human-readable local time for a message, e.g. "14:05". */
function formatTime(value?: string): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export const ChatMessage = memo(function ChatMessage({
  id,
  role,
  content,
  toolCalls,
  createdAt,
  complete,
  busy,
  onEdit,
  onRegenerate,
  onDownloadFile,
}: ChatMessageProps) {
  const isUser = role === 'user';
  // Only an assistant reply can be half an answer; a user turn is what the user
  // typed, in full, or it was never sent.
  const interrupted = !isUser && complete === false;
  const hasTools = !isUser && Boolean(toolCalls && toolCalls.length > 0);
  const runningTool = toolCalls?.find((call) => call.running)?.name;

  // The files the reply produced, gathered from the calls that wrote them.
  // They are shown next to the reply rather than inside the tool panel: the
  // panel is collapsed by default, and a download the user has to go looking
  // for is a download they do not make.
  const files = (toolCalls ?? [])
    .map((call) => call.file)
    .filter((file): file is ChatFile => Boolean(file));

  // The tables the calls produced. They are shown after the reply rather
  // than inside the tool panel: the panel is collapsed by default, and the
  // data is the point of the question.
  const views = (toolCalls ?? [])
    .map((call) => call.view)
    .filter((view): view is NonNullable<typeof view> => Boolean(view));

  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(content);
  const timestamp = formatTime(createdAt);

  // A streamed bubble grows as tokens arrive; leaving the editor open across a
  // regenerate would show the previous text as though it were editable.
  React.useEffect(() => {
    if (!editing) setDraft(content);
  }, [content, editing]);

  const canEdit = isUser && !busy && Boolean(onEdit) && content.length > 0;
  const canRegenerate = !isUser && !busy && Boolean(onRegenerate) && content.length > 0;

  const submitEdit = () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === content) {
      setEditing(false);
      setDraft(content);
      return;
    }
    setEditing(false);
    onEdit?.(id, trimmed);
  };

  return (
    <MessageContainer isUser={isUser}>
      {/* The bubble may use up to 90% of the message area. The margin keeps a
          gutter at the edge for short messages, which otherwise sit flush
          against the panel. */}
      <Box
        sx={{
          maxWidth: '90%',
          margin: '0 8px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <MessagePaper isUser={isUser} elevation={1}>
          {hasTools && (
            <Stack spacing={0.5} sx={{ mb: 1 }}>
              {toolCalls!.map((call) => (
                <ToolCallPanel key={call.id} call={call} />
              ))}
            </Stack>
          )}

          {editing ? (
            <Box
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  submitEdit();
                }
                if (event.key === 'Escape') {
                  setEditing(false);
                  setDraft(content);
                }
              }}
            >
              <TextField
                autoFocus
                multiline
                fullWidth
                minRows={2}
                size="small"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder={t`Edit your question`}
                slotProps={{ htmlInput: { 'aria-label': t`Edit message` } }}
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1, justifyContent: 'flex-end' }}>
                {/*
                  These render as real <button> elements, but as bare caption
                  text they were about 18px tall -- below the 24px floor. They
                  are the only way out of the editor on a touch screen, so they
                  get a hit area and some breathing room.
                */}
                <Typography
                  component="button"
                  variant="caption"
                  onClick={() => {
                    setEditing(false);
                    setDraft(content);
                  }}
                  sx={{
                    border: 0,
                    background: 'transparent',
                    color: 'inherit',
                    cursor: 'pointer',
                    opacity: 0.8,
                    // Negative margin keeps the visual position identical to the
                    // un-padded text while enlarging what can be tapped.
                    mx: -1,
                    px: 1,
                    minHeight: 32,
                    '@media (pointer: coarse)': { minHeight: 44 },
                  }}
                >
                  <Trans>Cancel</Trans>
                </Typography>
                <Typography
                  component="button"
                  variant="caption"
                  onClick={submitEdit}
                  sx={{
                    border: 0,
                    background: 'transparent',
                    color: 'inherit',
                    cursor: 'pointer',
                    fontWeight: 600,
                    mx: -1,
                    px: 1,
                    minHeight: 32,
                    '@media (pointer: coarse)': { minHeight: 44 },
                  }}
                >
                  <Trans>Send</Trans>
                </Typography>
              </Stack>
            </Box>
          ) : content ? (
            <React.Suspense fallback={<Typography variant="body2">{content}</Typography>}>
              <Markdown>{content}</Markdown>
            </React.Suspense>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              <ChatSpinner size={12} sx={{ mr: 1 }} />
              {runningTool ? t`Running ${runningTool}…` : t`Thinking…`}
            </Typography>
          )}

          {views.length > 0 && (
            <React.Suspense fallback={null}>
              {views.map((view, index) => (
                <ChatToolView key={index} view={view} />
              ))}
            </React.Suspense>
          )}

          {files.length > 0 && onDownloadFile && (
            <ChatFileList files={files} onDownload={onDownloadFile} busy={busy} />
          )}
        </MessagePaper>

        {!editing && (timestamp || interrupted || canEdit || canRegenerate) && (
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              mt: 0.25,
              px: 1,
              alignItems: 'center',
              justifyContent: isUser ? 'flex-end' : 'flex-start',
              minHeight: 28,
            }}
          >
            {timestamp && (
              <Typography variant="caption" color="text.secondary">
                {timestamp}
              </Typography>
            )}
            {interrupted && (
              <Tooltip title={t`This reply was cut short. The text is all that was received.`}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}
                >
                  <MdiIcon d={mdiAlertCircleOutline} size={12} />
                  <Trans>stopped</Trans>
                </Typography>
              </Tooltip>
            )}
            {canEdit && (
              <Tooltip title={t`Edit and resend`}>
                <IconButton
                  size="small"
                  aria-label={t`Edit message`}
                  onClick={() => {
                    setDraft(content);
                    setEditing(true);
                  }}
                >
                  <MdiIcon d={mdiPencilOutline} size={14} />
                </IconButton>
              </Tooltip>
            )}
            {canRegenerate && (
              <Tooltip title={t`Regenerate reply`}>
                <IconButton
                  size="small"
                  aria-label={t`Regenerate reply`}
                  onClick={() => onRegenerate?.()}
                >
                  <MdiIcon d={mdiRefresh} size={14} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )}
      </Box>
    </MessageContainer>
  );
});
